import { StemType, TICKS_PER_LOOP } from '@arpeggi/shared'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { clone } from 'mobx-state-tree'
import { useContext, useRef } from 'react'
import { useDrop } from 'react-dnd'
import { useTreeState } from '../../../stores/Root'
import { IClipStore } from '../../../stores/song/clip/ClipStore'
import { IAudioStemStore } from '../../../stores/song/stem/AudioStemStore'
import { undoManager } from '../../../stores/StudioStore'
import { gray3, gray4 } from '../../platform/color'
import { ClipboardContext } from '../../studio/Context'
import { CopyTypes } from '../StudioConstants'
import { StudioDraggableTypes } from '../StudioUtils'

const useAudioStemBar = (stemIndex: number, section: number, instrumentLoopWidthPX: number) => {
  const flags = useFlags()
  const ref = useRef<HTMLDivElement>()
  const { studio } = useTreeState()
  const { clipboard } = useContext(ClipboardContext)
  const stem = studio.song.stems[stemIndex] as IAudioStemStore
  const songLengthIn48n = studio.song.songLengthIn48n
  const fill = gray3
  const darkFill = gray4
  let barCount = 16
  if (instrumentLoopWidthPX < 100) barCount = 4
  else if (instrumentLoopWidthPX < 150) barCount = 8
  else if (instrumentLoopWidthPX < 250) barCount = 16
  else if (instrumentLoopWidthPX < 400) barCount = 32
  else barCount = 64

  const acceptedAudioStemTypes = [StudioDraggableTypes.SAMPLE]
  if (flags.stemRemixingAsClips) {
    acceptedAudioStemTypes.push(StudioDraggableTypes.STEM)
  }
  const [{ isOver }, connectDrop] = useDrop(
    () => ({
      accept: !stem.isRemixed ? acceptedAudioStemTypes : [],
      drop: async (item: any, monitor) => {
        if (!ref.current) return
        const dropOffsetTicks =
          (TICKS_PER_LOOP *
            (monitor.getClientOffset().x - ref.current.getBoundingClientRect().left)) /
          instrumentLoopWidthPX
        if (item.type === StudioDraggableTypes.SAMPLE) {
          await studio.addClipFromSampleToAudioStem(
            item.sample,
            stemIndex,
            section * TICKS_PER_LOOP + dropOffsetTicks
          )
        } else if (item.type === StudioDraggableTypes.STEM) {
          await studio.addClipFromStemToAudioStem(
            item.stem.id,
            stemIndex,
            section * TICKS_PER_LOOP + dropOffsetTicks
          )
        }
        const stem = studio.song.stems[stemIndex] as IAudioStemStore
      },
      collect: (monitor) => ({
        isOver: !!monitor.isOver()
      })
    }),
    [stemIndex, section, acceptedAudioStemTypes, instrumentLoopWidthPX]
  )

  const pasteClip = () => {
    if (!clipboard) return

    undoManager.startGroup(() => {
      studio.disableAudioUpdates()
      if (clipboard.type === CopyTypes.CLIP) {
        const clip: IClipStore = clipboard.value
        const newClip = clone(clip)
        newClip.generateClipID()
        newClip.moveToSection(section, songLengthIn48n)
        studio.addClipToStem(stemIndex, newClip)
      } else if (clipboard.type === CopyTypes.CLIPS) {
        const clips: IClipStore[][] = clipboard.value

        // for maintaining the structure between the clips once pasted
        const clipOffset = Math.min.apply(
          null,
          clips.map((clipsArr) =>
            Math.min.apply(
              null,
              clipsArr.map((clip) => clip.startInComposition48n)
            )
          )
        )
        clips.forEach((clipsArr, relativeStemIndex) => {
          const stemIndexToAddTo = relativeStemIndex + stemIndex
          if (stemIndexToAddTo > studio.song.stems.length) return
          if (studio.song.stems[stemIndexToAddTo].type() !== StemType.AUDIO) return
          clipsArr.forEach((copiedClip) => {
            const newClip = clone(copiedClip)
            newClip.generateClipID()
            const newClipStart = newClip.startInComposition48n
            newClip.moveToSection(section, songLengthIn48n)
            newClip.move48n(newClipStart - clipOffset, songLengthIn48n)
            studio.addClipToStem(stemIndex + relativeStemIndex, newClip)
          })
        })
      }

      studio.enableAudioUpdates()
      undoManager.stopGroup()
    })
  }

  const loopSection = () => studio.loopSection(section)

  connectDrop(ref)
  return { fill, darkFill, ref, barCount, pasteClip, loopSection }
}

export default useAudioStemBar
